import { isNil } from 'lodash';
import { useEffect, useRef } from 'react';
import { View as RNView, ScrollView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { useCardBlockScrollContext } from '@shared/components/blocks/contexts/scroll';
import { MBLoadingIndicator } from '@shared/components/loading';
import { useToast } from '@shared/components/toast';
import { FormErrors } from '@shared/scenes/entity/components/FormErrors';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { useChannelEventHandlers } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { CardFormTypeContext } from '../../cardFormTypeContext';
import { EntityForm } from '../../components/EntityForm';
import { SubformNavigationContext } from '../../components/fields/Subform/SubformEditField';

interface Props {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  saveAttempted?: boolean;
  style?: StyleProp<ViewStyle>;
  isAuthenticated?: boolean;
}

export function CardEntityCreateForm({
  components,
  fields,
  updateField,
  errors,
  workflowTemplateId,
  viewTemplateId,
  saveAttempted,
  isSaveSuccessful,
  savedEntity,
  style,
  isAuthenticated,
}: Props & {
  isSaveSuccessful?: boolean;
  savedEntity?: API.WorkflowEntity;
}) {
  const { goBack } = useNavigator();
  const toast = useToast();
  const { onOpenCard } = useChannelEventHandlers();
  const isNavigating = useRef(false);

  useEffect(() => {
    if (isSaveSuccessful && !isNil(savedEntity) && !isNavigating.current) {
      isNavigating.current = true;
      goBack();
      toast.show({
        icon: 'check-filled',
        message: i18n('card.create.success'),
        actions: [
          {
            label: 'view',
            onPress: () => onOpenCard(savedEntity),
          },
        ],
      });
    }
  }, [isSaveSuccessful, goBack, toast, onOpenCard, savedEntity]);

  return (
    <CardEntityCreateFormContent
      components={components}
      fields={fields}
      updateField={updateField}
      errors={errors}
      workflowTemplateId={workflowTemplateId}
      viewTemplateId={viewTemplateId}
      saveAttempted={saveAttempted}
      style={style}
      isAuthenticated={isAuthenticated}
    />
  );
}

function CardEntityCreateFormContent({
  workflowTemplateId,
  viewTemplateId,
  fields,
  updateField,
  components,
  errors,
  saveAttempted,
  style = {},
  isAuthenticated = true,
}: Props) {
  const scrollView = useCardBlockScrollContext();

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return <MBLoadingIndicator fill />;
  }

  return (
    <CardFormTypeContext.Provider value="CREATE">
      <CardRenderContext.Provider value="form">
        <SubformNavigationContext
          fields={fields}
          updateField={updateField}
          saveAttempted={saveAttempted}
          apiErrors={errors}
        >
          <RNView style={styles.formPane}>
            <div style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
              <FormErrors errors={errors} />
            </div>
            <ScrollView
              ref={scrollView}
              style={{ width: '100%' }}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              contentContainerStyle={{ flexGrow: 1 }}
            >
              <EntityForm
                fields={fields}
                updateField={updateField}
                components={components}
                errors={errors}
                workflowTemplateId={workflowTemplateId}
                viewTemplateId={viewTemplateId}
                style={style}
                isAuthenticated={isAuthenticated}
              />
            </ScrollView>
          </RNView>
        </SubformNavigationContext>
      </CardRenderContext.Provider>
    </CardFormTypeContext.Provider>
  );
}

const styles = StyleSheet.create({
  formPane: {
    flex: 1,
  },
});
